import React, { useCallback, useEffect, useState } from "react";
import {
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  Typography,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Paper,
  Grid,
  Card,
  Pagination,
  TextField,
  Box,
} from "@mui/material";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import EmptyData from "components/EmptyData";
import LongMenuButton from "Utils/userButton";
import { useSelector } from "react-redux";
import ImageModal from "components/ImageModal";
import apiClient from "../../Utils/apiClient";
import socketService from "../../socket/newsocketService";

// Legacy Firebase implementation kept during the backend migration:
// import { getDatabase, ref, query, get, orderByChild } from "firebase/database";

const USERS_PER_PAGE = 10;

const normalizeText = (value) => String(value ?? "").trim();

const normalizeUserRecord = (user) => ({
  ...user,
  ...(user?.user || {}),
  id: user?.id || user?._id?.toString?.() || user?._id || user?.user?.id || user?.user?._id || "",
  createdAtValue: new Date(user?.createdAt || user?.createdDate || user?.user?.createdAt || 0).getTime() || 0,
});

const getStoredAdminIdentity = () => {
  const storedUser = JSON.parse(localStorage.getItem("user") || "{}");

  return {
    storedUser,
    userId: localStorage.getItem("userId") || storedUser?.userId || storedUser?.id || storedUser?._id || "",
  };
};

const Users = () => {
  const getUserState = useSelector((state) => state?.data?.getUserState);
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalUsers, setTotalUsers] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [checkSuperAdmin, setCheckSuperAdmin] = useState(null);
  const [adminState, setAdminState] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [onClickImageData, setOnClickImageData] = useState("");
  const [activeTab, setActiveTab] = useState(0); // 0: Active Users, 1: Deleted Users
  const [restoreModalOpen, setRestoreModalOpen] = useState(false);
  const [selectedUserToRestore, setSelectedUserToRestore] = useState(null);
  const [restoring, setRestoring] = useState(false);


  const fetchUsers = useCallback(async () => {
    const { storedUser, userId } = getStoredAdminIdentity();
    const isSuperAdmin = Boolean(storedUser?.superAdmin);
    const storedState = normalizeText(storedUser?.state || storedUser?.userState);
    const activeState = isSuperAdmin
      ? normalizeText(getUserState) && normalizeText(getUserState) !== "All State"
        ? normalizeText(getUserState)
        : ""
      : storedState;

    if (!userId) {
      return;
    }

    setLoading(true);
    setCheckSuperAdmin(isSuperAdmin);
    setAdminState(storedState);

    try {
      const params = {
        page: currentPage,
        limit: USERS_PER_PAGE,
      };

      if (activeState) {
        params.state = activeState;
      }

      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      if (activeTab === 1) {
        params.status = "deleted";
      }

      const response = await apiClient.get("/users", { params });
      const payload = response?.data;
      const pagination = response?.pagination || {};
      const records = Array.isArray(payload) ? payload : [];

      const normalizedUsers = records
        .map(normalizeUserRecord)
        .filter((user) => {
          const rowState = normalizeText(user?.state || user?.userState);
          return !activeState || rowState === activeState;
        });

      setUsers(normalizedUsers);
      setTotalUsers(Number(pagination?.total ?? normalizedUsers.length));
    } catch (error) {
      console.error("[Users] failed to fetch users", error);
      setUsers([]);
      setTotalUsers(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, getUserState, searchTerm, activeTab]);

  const getSubscriptionStatus = (endDate) => {
    const currentDate = new Date();
    const endSubscriptionDate = new Date(endDate);

    if (endSubscriptionDate < currentDate) {
      return "expired";
    }

    const oneWeekBeforeEnd = new Date(endSubscriptionDate);
    oneWeekBeforeEnd.setDate(oneWeekBeforeEnd.getDate() - 7);

    if (currentDate >= oneWeekBeforeEnd && currentDate <= endSubscriptionDate) {
      return "aboutToExpire";
    }

    return "active";
  };

  useEffect(() => {
    const { storedUser, userId } = getStoredAdminIdentity();

    if (!userId) {
      navigate("/authentication/sign-in");
      return;
    }

    void socketService.initializeSocket(
      userId,
      storedUser?.state,
      {
        isSuperAdmin: storedUser?.superAdmin,
        web: "true",
      },
    ).catch(() => {});

    void fetchUsers();
  }, [fetchUsers, navigate]);

  useEffect(() => {
    setCurrentPage(1);
  }, [getUserState]);


  const handleOpenRestore = (user) => {
    setSelectedUserToRestore(user);
    setRestoreModalOpen(true);
  };

  const handleConfirmRestore = async () => {
    if (!selectedUserToRestore) return;
    const userId = selectedUserToRestore?.id || selectedUserToRestore?._id;
    try {
      setRestoring(true);
      await apiClient.post(`/admin/users/${userId}/restore`);
      const { toast } = require("react-toastify");
      toast.success("User restored successfully!");
      setRestoreModalOpen(false);
      setSelectedUserToRestore(null);
      void fetchUsers();
    } catch (error) {
      console.error("[Users] restore error:", error);
      const { toast } = require("react-toastify");
      toast.error(error?.message || "Failed to restore user");
    } finally {
      setRestoring(false);
    }
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const userDeleted = () => {
    void fetchUsers();
  };

  const handleImageClick = (url) => {
    setIsModalOpen(true);
    setOnClickImageData(url);
  };
  const onClose = () => {
    setIsModalOpen(false);
  };

  const getTopEmergencyContacts = (contacts) => {
    if (!contacts) return [];

    const uniqueMap = new Map();

    Object.values(contacts)
      .sort((a, b) => b.createdAt - a.createdAt)
      .forEach((contact) => {
        if (!uniqueMap.has(contact.phoneNumber)) {
          uniqueMap.set(contact.phoneNumber, contact);
        }
      });

    return Array.from(uniqueMap.values()).slice(0, 3);
  };

  return (
      <DashboardLayout>
        <MDBox pt={7} pb={3}>
          <ToastContainer />
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={4} px={2}>
            <Tabs
              value={activeTab}
              onChange={(e, val) => {
                setActiveTab(val);
                setCurrentPage(1);
              }}
              textColor="primary"
              indicatorColor="primary"
              sx={{ bgcolor: "#fff", borderRadius: 2, px: 1, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}
            >
              <Tab label="Active Users" sx={{ fontWeight: 600, fontSize: "14px", textTransform: "none" }} />
              <Tab label="Deleted Users" sx={{ fontWeight: 600, fontSize: "14px", textTransform: "none" }} />
            </Tabs>
            <TextField
              label="Search by name, email, or phone"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              sx={{ width: 300, bgcolor: "#fff", borderRadius: 1 }}
            />
          </Box>
        <Grid container spacing={6} style={{ position: "relative", minHeight: "60vh" }}>
          {loading ? (
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              Loading...
            </div>
          ) : (
            <Grid item xs={12}>
              <Card>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={1}
                  variant="gradient"
                  bgColor="info"
                  borderRadius="lg"
                  coloredShadow="info"
                  sx={{
                    textAlign: "center",
                  }}
                >
                  <MDTypography
                    variant="h6"
                    color="white"
                    sx={{
                      fontWeight: "bold",
                      fontSize: "1.5rem",
                    }}
                  >
                    {checkSuperAdmin
                      ? getUserState === ""
                        ? "ALL STATE "
                        : `${getUserState?.toUpperCase()} STATE `
                      : `${adminState?.toUpperCase()} STATE `}
                    {activeTab === 1 ? 'DELETED USERS DASHBOARD' : 'USERS DASHBOARD'}
                  </MDTypography>
                </MDBox>
                {users?.length > 0 ? (
                  <MDBox>
                    <TableContainer component={Paper}>
                      <Table aria-label="user table" sx={{ display: "table !important", minWidth: 1800, "& thead, & tbody": { display: "table-row-group !important" }, "& tr": { display: "table-row !important" }, "& td, & th": { display: "table-cell !important", fontSize: "13px", padding: "8px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" } }}>
                        <TableHead>
                          {activeTab === 1 ? (
                            <TableRow>
                              <TableCell style={{ fontWeight: "bold", color: "#1C87F9", fontSize: "14px" }}>SR</TableCell>
                              <TableCell style={{ fontWeight: "bold", color: "#1C87F9", fontSize: "14px" }}>Name</TableCell>
                              <TableCell style={{ fontWeight: "bold", color: "#1C87F9", fontSize: "14px" }} align="center">Phone</TableCell>
                              <TableCell style={{ fontWeight: "bold", color: "#1C87F9", fontSize: "14px" }} align="center">Deleted By</TableCell>
                              <TableCell style={{ fontWeight: "bold", color: "#1C87F9", fontSize: "14px" }} align="center">Deleted At</TableCell>
                              <TableCell style={{ fontWeight: "bold", color: "#1C87F9", fontSize: "14px" }} align="center">Reason</TableCell>
                              <TableCell style={{ fontWeight: "bold", color: "#1C87F9", fontSize: "14px" }} align="center">Action</TableCell>
                            </TableRow>
                          ) : (
                          <TableRow>
                            <TableCell
                              style={{ fontWeight: "bold", color: "#1C87F9", fontSize: "14px" }}
                            >
                              SR
                            </TableCell>
                            <TableCell
                              style={{ fontWeight: "bold", color: "#1C87F9", fontSize: "14px" }}
                            >
                              Name
                            </TableCell>
                            <TableCell
                              style={{ fontWeight: "bold", color: "#1C87F9", fontSize: "14px" }}
                              align="center"
                            >
                              Date of Registration
                            </TableCell>
                            <TableCell
                              style={{ fontWeight: "bold", color: "#1C87F9", fontSize: "14px" }}
                              align="center"
                            >
                              Email
                            </TableCell>
                            <TableCell
                              style={{ fontWeight: "bold", color: "#1C87F9", fontSize: "14px" }}
                              align="center"
                            >
                              Phone
                            </TableCell>
                            <TableCell
                              style={{ fontWeight: "bold", color: "#1C87F9", fontSize: "14px" }}
                              align="center"
                            >
                              Gender
                            </TableCell>
                            <TableCell
                              style={{ fontWeight: "bold", color: "#1C87F9", fontSize: "14px" }}
                              align="center"
                            >
                              Address
                            </TableCell>
                            <TableCell
                              style={{ fontWeight: "bold", color: "#1C87F9", fontSize: "14px" }}
                              align="center"
                            >
                              State
                            </TableCell>
                            <TableCell
                              style={{ fontWeight: "bold", color: "#1C87F9", fontSize: "14px" }}
                              align="center"
                            >
                              User Type
                            </TableCell>
                            <TableCell
                              style={{ fontWeight: "bold", color: "#1C87F9", fontSize: "14px" }}
                              align="center"
                            >
                              Karzame Tokens
                            </TableCell>
                            <TableCell
                              style={{ fontWeight: "bold", color: "#1C87F9", fontSize: "14px" }}
                              align="center"
                            >
                              UserId Card
                            </TableCell>
                            <TableCell
                              style={{ fontWeight: "bold", color: "#1C87F9", fontSize: "14px" }}
                              align="center"
                            >
                              Utility Bill Image
                            </TableCell>
                            <TableCell
                              style={{ fontWeight: "bold", color: "#1C87F9", fontSize: "14px" }}
                              align="center"
                            >
                              Device Model
                            </TableCell>
                            <TableCell
                              style={{ fontWeight: "bold", color: "#1C87F9", fontSize: "14px" }}
                              align="center"
                            >
                              subscription
                            </TableCell>
                              <TableCell
                                style={{ fontWeight: "bold", color: "#1C87F9", fontSize: "14px" }}
                                align="center"
                              >
                              Reference Code
                            </TableCell>
                            <TableCell
                              style={{ fontWeight: "bold", color: "#1C87F9", fontSize: "14px" }}
                              align="center"
                            >
                              Emergency Contact
                            </TableCell>
                            <TableCell
                              style={{ fontWeight: "bold", color: "#1C87F9", fontSize: "14px" }}
                              align="center"
                            >
                              Action
                            </TableCell>
                          </TableRow>
                          )}
                        </TableHead>
                        <TableBody>
                            {users.map((user, index) =>
                              activeTab === 1 ? (
                                <TableRow key={user.id || index} sx={{ height: "auto" }}>
                                  <TableCell align="center" sx={{ padding: "6px" }}>
                                    {(currentPage - 1) * USERS_PER_PAGE + index + 1}
                                  </TableCell>
                                  <TableCell component="th" scope="row" sx={{ padding: "6px" }}>
                                    <Box display="flex" alignItems="center">
                                      <Avatar src={user.userImage} sx={{ mr: 1, width: 32, height: 32 }} />
                                      {user.userName || "—"}
                                    </Box>
                                  </TableCell>
                                  <TableCell align="center" sx={{ padding: "6px", fontSize: "14px" }}>
                                    {user.userPhone || "—"}
                                  </TableCell>
                                  <TableCell align="center" sx={{ padding: "6px", fontSize: "14px" }}>
                                    {user.deleted_by || "—"}
                                  </TableCell>
                                  <TableCell align="center" sx={{ padding: "6px", fontSize: "14px" }}>
                                    {user.deleted_at ? new Date(user.deleted_at).toLocaleString() : "—"}
                                  </TableCell>
                                  <TableCell align="center" sx={{ padding: "6px", fontSize: "14px", textTransform: "capitalize" }}>
                                    <Box
                                      component="span"
                                      sx={{
                                        px: 1.5,
                                        py: 0.5,
                                        borderRadius: 2,
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        bgcolor: user.delete_reason === "fraud" ? "#ffebee" : "#ede7f6",
                                        color: user.delete_reason === "fraud" ? "#c62828" : "#4527a0",
                                      }}
                                    >
                                      {user.delete_reason || "—"}
                                    </Box>
                                  </TableCell>
                                  <TableCell align="center" sx={{ padding: "6px" }}>
                                    <Button
                                      variant="contained"
                                      size="small"
                                      onClick={() => handleOpenRestore(user)}
                                      sx={{
                                        bgcolor: "#2e7d32 !important",
                                        color: "#fff !important",
                                        textTransform: "none",
                                        fontWeight: 600,
                                        "&:hover": { bgcolor: "#1b5e20 !important" },
                                      }}
                                    >
                                      Restore
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ) : (
                                <TableRow key={index} sx={{ height: "auto" }}>
                                <TableCell sx={{ padding: "6px" }}>
                                  {index + 1 + (currentPage - 1) * USERS_PER_PAGE}
                                </TableCell>
                                <TableCell sx={{ padding: "6px", fontSize: "14px" }} >
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Avatar
                                      src={user?.userImage || user?.image || user?.profilePic}
                                      alt={user?.userName || user?.name || user?.fullName}
                                    />
                                    <span>{user?.userName || user?.name || user?.fullName || "N/A"}</span>
                                  </div>
                                </TableCell>
                              <TableCell align="center" sx={{ padding: "6px", fontSize: "14px" }}
                              >
                                  {new Date(user?.createdAt || user?.createdDate || Date.now()).toLocaleString()}
                                </TableCell>

                                <TableCell align="center" sx={{ padding: "6px", fontSize: "14px" }}
                                >
                                  {user?.userEmail || user?.email || "N/A"}
                                </TableCell>
                                <TableCell align="center" sx={{ padding: "6px", fontSize: "14px" }}
                                >
                                  {user?.userPhone || user?.phone || user?.mobile || "N/A"}
                                </TableCell>
                                <TableCell align="center" sx={{ padding: "6px", fontSize: "14px" }}
                                >
                                  {user?.userGender || user?.gender || "N/A"}
                                </TableCell>
                                <TableCell align="center" sx={{ padding: "6px", fontSize: "14px" }}
                                >
                                  {user?.userAddress || user?.address || "N/A"}
                                </TableCell>

                                <TableCell align="center" sx={{ padding: "6px", fontSize: "14px" }}
                                >
                                  {user?.state || user?.userState || "N/A"}
                                </TableCell>
                                <TableCell align="center" sx={{ padding: "6px", fontSize: "14px" }}
                                >
                                  {user?.userGSM}
                                </TableCell>
                                <TableCell align="center" sx={{ padding: "6px", fontSize: "14px" }}
                                >
                                  {user?.token}
                                </TableCell>

                                <TableCell
                                  style={{ fontWeight: "bold", color: "#1C87F9", fontSize: "14px" }}
                                  align="center"
                                >
                                  {user?.userIdCard ? (
                                    <img
                                      onClick={() => {
                                        handleImageClick(user?.userIdCard);
                                      }}
                                      src={user?.userIdCard}
                                      alt="Id Card"
                                      style={{ width: "30px", height: "30px", marginBottom: "5px" }}
                                    />
                                  ) : null}
                                </TableCell>

                                <TableCell align="center" sx={{ padding: "6px", fontSize: "14px" }}
                                >
                                  {user?.utilityBillImage ? (
                                    <>
                                      <a
                                        href={user.utilityBillImage}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        <img
                                          src={user.utilityBillImage}
                                          alt="Utility Bill"
                                          style={{
                                            width: "30px",
                                            height: "30px",
                                            marginBottom: "5px",
                                            cursor: "pointer",
                                            border: `2px solid ${
                                              user.addressVerified ? "green" : "red"
                                            }`,
                                            borderRadius: "4px",
                                          }}
                                        />
                                      </a>
                                      <div
                                        style={{
                                          fontSize: "9px",
                                          fontWeight: "bold",
                                          color: user.addressVerified ? "green" : "red",
                                          backgroundColor: user.addressVerified
                                            ? "#e6f4ea"
                                            : "#fdecea",
                                          padding: "2px 6px",
                                          borderRadius: "10px",
                                          marginTop: "4px",
                                          display: "inline-block",
                                        }}
                                      >
                                        {user.addressVerified
                                          ? "Address Verified"
                                          : "Address Unverified"}
                                      </div>
                                    </>
                                  ) : null}
                                </TableCell>

                                <TableCell align="center" sx={{ padding: "6px", fontSize: "14px" }}
                                >
                                  {user?.deviceModel}
                                </TableCell>

                                <TableCell align="center" sx={{
                                    padding: "6px",
                                    fontSize: "14px",
                                    fontWeight: "bold",
                                    color: (() => {
                                      const sub = user?.activesubscription;
                                      if (sub?.type && sub.type !== "Basic" && sub.end) {
                                        const status = getSubscriptionStatus(sub.end);
                                        if (status === "active") return "green";
                                        if (status === "expired") return "red";
                                        if (status === "aboutToExpire") return "orange";
                                      }
                                      return "inherit";
                                    })(),
                                  }}
                                >
                                  {user?.activesubscription?.type || "—"}
                                </TableCell>

                                <TableCell align="center" sx={{ padding: "6px", fontSize: "14px" }}
                                >
                                  {user?.refrenceCode}
                                </TableCell>

                                <TableCell align="center" sx={{ padding: "6px", fontSize: "12px" }}
                                >
                                  {getTopEmergencyContacts(user?.EmergencyContacts).length > 0 ? (
                                    getTopEmergencyContacts(user?.EmergencyContacts).map(
                                      (contact, idx) => (
                                        <Box key={idx}>
                                          <div>{contact?.phoneNumber}</div>
                                        </Box>
                                      )
                                    )
                                  ) : (
                                    <span>—</span>
                                  )}
                                </TableCell>

                                <TableCell align="center" sx={{ padding: "6px" }}
                                >
                                  <LongMenuButton
                                    userId={user.id}
                                    userData={user}
                                    userDeleted={userDeleted}
                                    updateMemebrshipCheck={(checkMembershipUpdate) => {
                                      if (checkMembershipUpdate) fetchUsers();
                                    }}
                                  />
                                </TableCell>
                              </TableRow>
                              )
                            )}
                          </TableBody>
                      </Table>
                    </TableContainer>
                    <MDBox display="flex" justifyContent="center" mt={3} mb={3}>
                      <Pagination
                        count={Math.ceil(totalUsers / USERS_PER_PAGE)}
                        page={currentPage}
                        onChange={handlePageChange}
                        color="primary"
                      />
                    </MDBox>
                  </MDBox>
                ) : (
                  <EmptyData />
                )}
              </Card>
            </Grid>
          )}
        </Grid>
      </MDBox>
      <ImageModal onClickImageData={onClickImageData} onClose={onClose} isModalOpen={isModalOpen} />
      <Dialog open={restoreModalOpen} onClose={() => !restoring && setRestoreModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: "bold" }}>Restore User Account</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Are you sure you want to restore this user account?
            </Typography>
            <Box sx={{ my: 1.5, p: 1.5, bgcolor: "#f8f9fa", borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Name: <span style={{ fontWeight: 400 }}>{selectedUserToRestore?.userName || "—"}</span>
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 0.5 }}>
                Phone: <span style={{ fontWeight: 400 }}>{selectedUserToRestore?.userPhone || "—"}</span>
              </Typography>
            </Box>
            <Typography variant="caption" color="textSecondary">
              The user will keep their ID, tokens, active subscription, and reference code.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRestoreModalOpen(false)} disabled={restoring} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmRestore}
            disabled={restoring}
            variant="contained"
            sx={{
              bgcolor: "#2e7d32 !important",
              color: "#fff !important",
              "&:hover": { bgcolor: "#1b5e20 !important" },
            }}
          >
            {restoring ? "Restoring..." : "Confirm Restore"}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default Users;
